import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { pickImage } from "@/data/imagePool";
import { I } from "@/data/imageDefaults";

const points = [
  "One-stop partner for branding, print, events & gifting",
  "In-house design, production and on-site execution",
  "Trusted by 100+ corporates across the UAE",
  "Quality, creativity and on-time delivery — always",
];

export default function About() {
  const image1 = pickImage("corp", "Corporate Team Collaborating", 0);
  const src1 = image1.includes("photos") || image1.includes("unsplash") || image1.includes("picsum") ? I.corp : image1;

  const image2 = pickImage("press1", "Premium printing press", 0);
  const src2 = image2.includes("photos") || image2.includes("unsplash") || image2.includes("picsum") ? I.press1 : image2;
  return (
    <section id="about" data-testid="about-section" className="relative bg-white py-24 md:py-32">
      <div className="pz-container grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        {/* Image collage */}
        <div className="relative h-[520px] sm:h-[600px]">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="absolute top-0 left-0 w-[68%] h-[70%] rounded-3xl overflow-hidden shadow-2xl"
          >
            <img
              src={src1}
              alt="Corporate team collaborating"
              className="w-full h-full object-cover"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="absolute bottom-0 right-0 w-[62%] h-[58%] rounded-3xl overflow-hidden shadow-2xl ring-8 ring-white"
          >
            <img
              src={src2}
              alt="Premium printing press"
              className="w-full h-full object-cover"
            />
          </motion.div>

          {/* Floating badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="absolute top-[38%] right-[-10px] sm:right-[-20px] bg-[#0F172A] text-white rounded-2xl px-6 py-5 shadow-2xl"
          >
            <div className="font-display text-3xl font-bold text-[#F59E0B]">UAE</div>
            <div className="text-xs uppercase tracking-[0.22em] text-white/70 mt-1">Made & Delivered</div>
          </motion.div>

          {/* Gold corner accent */}
          <div className="absolute -top-4 -left-4 w-20 h-20 border-l-4 border-t-4 border-[#F59E0B] rounded-tl-3xl" />
          <div className="absolute -bottom-4 -right-4 w-20 h-20 border-r-4 border-b-4 border-[#2563EB] rounded-br-3xl" />
        </div>

        {/* Text */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <span className="pz-overline">About Printzee</span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-[#0F172A]">
            A reliable one-stop partner<br />for branding, print &amp; events.
          </h2>
          <div className="pz-gold-line mt-6" />
          <p className="mt-7 text-slate-600 text-base sm:text-lg leading-relaxed">
            Printzee.ae is a professional branding, printing and event solutions company delivering high-quality visual
            and customized experiences for businesses, agencies and organizations. We help brands enhance their presence
            through impactful displays, printing and well-executed events.
          </p>
          <p className="mt-5 text-slate-600 text-base sm:text-lg leading-relaxed">
            With a commitment to quality, creativity and timely delivery, we serve as a reliable one-stop solution for
            branding, printing and corporate gifting requirements across the UAE.
          </p>

          <ul className="mt-8 grid sm:grid-cols-2 gap-3">
            {points.map((p) => (
              <li key={p} className="flex items-start gap-3" data-testid={`about-point-${p.slice(0,8)}`}>
                <span className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-[#2563EB]/10 flex items-center justify-center">
                  <Check className="w-3 h-3 text-[#2563EB]" strokeWidth={3} />
                </span>
                <span className="text-slate-700 text-sm">{p}</span>
              </li>
            ))}
          </ul>

          <a
            href="#services"
            data-testid="about-cta"
            className="mt-10 inline-flex items-center gap-2 font-medium text-[#0F172A] pz-link-underline"
          >
            See our services <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
